import React from "react";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";

import { AccountType } from "../../../models/AccountData";
import { LocationData } from "../../../models/LocationData";
import {
  CollectLocationDataType,
  EncodeType
} from "../../../models/LocationDataDownload";
import DataSvc from "../../../services/dataSvc";
import { downloadCsv } from "../../../utils/csvUtils";
import { OptionalRender } from "../../Common/OptionalRender";

import "./styles.scss";

type IFacilityDataDownloadProps = {
  locationData: LocationData;
};

export const FacilityDataDownload: React.FunctionComponent<
  IFacilityDataDownloadProps
> = ({ locationData }) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`facilityDetailsPage.facilityDataDownload.${key}`);

  const [isDownloading, setIsDownloading] = React.useState<{
    utf8: boolean;
    shiftJis: boolean;
  }>({
    utf8: false,
    shiftJis: false
  });

  const [isDownloadingProcessed, setIsDownloadingProcessed] = React.useState<{
    utf8: boolean;
    shiftJis: boolean;
  }>({
    utf8: false,
    shiftJis: false
  });

  const handleDownload = async (encoding: EncodeType) => {
    const encodingKey = encoding === EncodeType.UTF8 ? "utf8" : "shiftJis";

    setIsDownloading((prev) => ({ ...prev, [encodingKey]: true }));

    try {
      const blob = await DataSvc.downloadLocationData({
        location_id: locationData.id,
        data_type: CollectLocationDataType.reviews,
        encode: encoding
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const encodingSuffix =
        encoding === EncodeType.UTF8 ? "utf8" : "shift_jis";
      const filename = `${locationData.name}_reviews_${encodingSuffix}.csv`;

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      ReactGA.event({
        category: "facility_details",
        action: "Download Facility Data",
        label: `${CollectLocationDataType.reviews}_${encoding}`
      });
    } catch (error) {
      console.error(`Download failed for encoding ${encoding}:`, error);
    } finally {
      setIsDownloading((prev) => ({ ...prev, [encodingKey]: false }));
    }
  };

  const handleDownloadProcessed = async (encoding: EncodeType) => {
    const encodingKey = encoding === EncodeType.UTF8 ? "utf8" : "shiftJis";
    setIsDownloadingProcessed((prev) => ({ ...prev, [encodingKey]: true }));

    try {
      const reviews = await DataSvc.getProcessedReviews(
        String(locationData.id)
      );

      if (!reviews || reviews.length === 0) {
        console.warn("No processed reviews available");
        setIsDownloadingProcessed((prev) => ({
          ...prev,
          [encodingKey]: false
        }));
        return;
      }

      // Create CSV content with specified fields
      const header = [
        "Review Date",
        "Language",
        "Rating",
        "Comment",
        "Translation"
      ].join(",");

      const csvRows = reviews.map((review: any) => {
        const reviewDate = review.review_date
          ? new Date(review.review_date).toISOString().split("T")[0]
          : "";
        const language = review.language || "";
        const rating = review.rating || "";
        const comment = review.comment
          ? `"${review.comment.replace(/"/g, '""')}"`
          : "";
        const translation = review.translation
          ? `"${review.translation.replace(/"/g, '""')}"`
          : "";

        return [reviewDate, language, rating, comment, translation].join(",");
      });

      const rows = [header, ...csvRows];
      const encodingSuffix =
        encoding === EncodeType.UTF8 ? "utf8" : "shift_jis";
      const filename = `${
        locationData.name
      }_processed_reviews_${encodingSuffix}_${
        new Date().toISOString().split("T")[0]
      }`;

      // Use csvUtils downloadCsv function
      const forWindows = encoding === EncodeType.SHIFT_JIS;
      downloadCsv(rows, filename, forWindows);

      ReactGA.event({
        category: "facility_details",
        action: "Download Processed Reviews",
        label: `${locationData.id}_${encodingSuffix}`
      });
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setIsDownloadingProcessed((prev) => ({ ...prev, [encodingKey]: false }));
    }
  };

  return (
    <OptionalRender mustBeOneOfRoles={[AccountType.ADMIN]}>
      <div className="detail-group facility-data-download">
        <div className="group-title">
          {t("section_name")}
          <div className="line"></div>
        </div>
        <small className="section-hint">{t("section_hint")}</small>

        <div className="white-border-panel download-section">
          <div className="download-item">
            <div className="download-item-content">
              <h4 className="download-title">{t("google_reviews_title")}</h4>
              <p className="download-description">{t("google_reviews_hint")}</p>
              <div className="download-buttons">
                <button
                  className="btn btn-border"
                  onClick={() => handleDownload(EncodeType.UTF8)}
                  disabled={isDownloading.utf8}
                >
                  {isDownloading.utf8 ? t("downloading") : t("download")}
                </button>
                <button
                  className="btn btn-border"
                  onClick={() => handleDownload(EncodeType.SHIFT_JIS)}
                  disabled={isDownloading.shiftJis}
                >
                  {isDownloading.shiftJis
                    ? t("downloading")
                    : t("download_jis")}
                </button>
              </div>
            </div>
          </div>

          <div className="download-item">
            <div className="download-item-content">
              <h4 className="download-title processed-reviews">
                {t("downloadProcessedReviews.title")}
              </h4>
              <p className="download-description">
                {t("downloadProcessedReviews.description")}
              </p>
              <div className="download-buttons">
                <button
                  className="btn btn-border"
                  onClick={() => handleDownloadProcessed(EncodeType.UTF8)}
                  disabled={isDownloadingProcessed.utf8}
                >
                  {isDownloadingProcessed.utf8
                    ? t("downloading")
                    : t("download")}
                </button>
                <button
                  className="btn btn-border"
                  onClick={() => handleDownloadProcessed(EncodeType.SHIFT_JIS)}
                  disabled={isDownloadingProcessed.shiftJis}
                >
                  {isDownloadingProcessed.shiftJis
                    ? t("downloading")
                    : t("download_jis")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </OptionalRender>
  );
};
